import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAssistantImage } from '../../../hooks/useAssistantImage';

export interface DisplayAttachment {
  path?: string;        // server-stored object path (auth-gated fetch)
  mime: string;
  name: string;
  localPreviewUrl?: string; // object URL for optimistic/just-uploaded images
}

/** Single thumbnail: uses the local preview when present, else fetches by path. */
const AttachmentThumb: React.FC<{ att: DisplayAttachment; onOpen: (src: string) => void }> = ({ att, onOpen }) => {
  const { data: fetched } = useAssistantImage(att.localPreviewUrl ? undefined : att.path);
  const src = att.localPreviewUrl ?? fetched ?? null;

  if (!src) {
    return (
      <div
        className="h-24 w-24 rounded-lg bg-white/5 border border-white/10 animate-pulse"
        data-test-id="attachment-thumb-loading"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(src)}
      className="h-24 w-24 rounded-lg overflow-hidden border border-white/10 hover:border-indigo-400 transition-colors"
      title={att.name}
      data-test-id="attachment-thumb"
    >
      <img src={src} alt={att.name} className="h-full w-full object-cover" />
    </button>
  );
};

/** Thumbnail grid for a message's image attachments, with a click-to-expand overlay. */
export const MessageAttachments: React.FC<{ attachments: DisplayAttachment[] }> = ({ attachments }) => {
  const [lightbox, setLightbox] = useState<string | null>(null);

  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-2" data-test-id="message-attachments">
        {attachments.map((att, i) => (
          <AttachmentThumb key={att.path ?? att.localPreviewUrl ?? i} att={att} onOpen={setLightbox} />
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-test-id="attachment-lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            onClick={() => setLightbox(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
        </div>
      )}
    </>
  );
};
